import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';

/**
 * DigitDropdownComponent
 *
 * Purpose
 * - Textbox-based component with mat-menu dropdown for selecting digits 0-9
 * - Supports both manual typing and dropdown selection
 * - Based on the textbox component structure with consistent styling
 * - Provides validation support and two-way data binding
 *
 * Visual states
 * - default: standard textbox with border
 * - focus: purple border (#DBACF5) with underline
 * - error: red background when validation fails
 * - disabled: grayed out appearance, no interaction
 * - open: border highlight when dropdown menu is open
 *
 * Features
 * - Optional title/label above the input
 * - Dropdown menu with digits 0-9 for quick selection
 * - Manual typing support in addition to dropdown selection
 * - Form validation integration
 * - Customizable width
 * - Readonly mode support
 * - Two-way data binding for form integration
 *
 * Inputs
 * - model: string (default: '') - The selected digit value (supports two-way binding)
 * - placeholder: string (default: '') - Placeholder text shown when input is empty
 * - title: string (default: '') - Label text displayed above the input
 * - textboxWidth: string (default: '200px') - Custom width for the input (e.g., '100%', '300px')
 * - disabled: boolean (default: false) - Disables interaction and applies disabled styling
 * - required: boolean (default: false) - Marks the field as required for validation
 * - readonly: boolean (default: false) - Makes the input readonly (dropdown still works)
 * - tabindex: number (default: 0) - Tab order for keyboard navigation
 * - valid: boolean (default: false) - External validation state
 *
 * Outputs
 * - modelChange: string - Emits the new value on change (supports [(model)] two-way binding)
 * - validChange: boolean - Emits validation state changes
 *
 * Usage examples
 *
 * <!-- Basic digit dropdown -->
 * <app-digit-dropdown
 *   [(model)]="selectedDigit"
 *   placeholder="Select a digit">
 * </app-digit-dropdown>
 *
 * <!-- With title and custom width -->
 * <app-digit-dropdown
 *   [(model)]="selectedDigit"
 *   title="Choose Digit"
 *   placeholder="0-9"
 *   [textboxWidth]="'150px'">
 * </app-digit-dropdown>
 *
 * <!-- Required field with validation -->
 * <app-digit-dropdown
 *   [(model)]="selectedDigit"
 *   title="Required Digit"
 *   [required]="true"
 *   [(valid)]="isValid"
 *   placeholder="Select 0-9">
 * </app-digit-dropdown>
 *
 * <!-- Disabled state -->
 * <app-digit-dropdown
 *   [(model)]="selectedDigit"
 *   title="Digit"
 *   [disabled]="true"
 *   placeholder="Disabled">
 * </app-digit-dropdown>
 *
 * <!-- Readonly mode (dropdown still works) -->
 * <app-digit-dropdown
 *   [(model)]="selectedDigit"
 *   title="Digit"
 *   [readonly]="true"
 *   placeholder="Readonly">
 * </app-digit-dropdown>
 *
 * <!-- Full width in form -->
 * <app-digit-dropdown
 *   [(model)]="selectedDigit"
 *   title="Select Digit"
 *   [textboxWidth]="'100%'"
 *   [required]="true"
 *   placeholder="Choose 0-9">
 * </app-digit-dropdown>
 *
 * <!-- In a form with validation -->
 * <form #myForm="ngForm">
 *   <app-digit-dropdown
 *     [(model)]="formData.digit"
 *     title="Digit Selection"
 *     [required]="true"
 *     [(valid)]="digitValid"
 *     placeholder="Select 0-9">
 *   </app-digit-dropdown>
 *   <button [disabled]="!digitValid || !myForm.valid">Submit</button>
 * </form>
 *
 * <!-- Component TypeScript example -->
 * export class MyComponent {
 *   selectedDigit: string = '';
 *   isValid: boolean = false;
 *   formData = { digit: '' };
 *   digitValid: boolean = false;
 *
 *   onDigitChange() {
 *     console.log('Selected digit:', this.selectedDigit);
 *   }
 * }
 */

@Component({
    selector: 'app-digit-dropdown',
    templateUrl: './digit-dropdown.component.html',
    styleUrls: ['./digit-dropdown.component.scss'],
    standalone: false
})
export class DigitDropdownComponent implements OnInit, OnChanges {
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  @Input() model: string;
  @Output() modelChange = new EventEmitter<string>();

  @Input() placeholder: string = "";
  @Input() title: string = "";
  @Input() textboxWidth: string = '200px';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() tabindex: number = 0;
  @Input() includeEmpty: number = 0;

  @Input() valid: boolean = false;
  @Output() validChange = new EventEmitter<boolean>();

  @Input() hasError: boolean = false;
  @Input() errorText: string = '';

  isOpen: boolean = false;
  digits: string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  constructor() {}

  ngOnInit(): void {
    this.digits = this.includeEmpty ? [''].concat(this.digits) : this.digits;
  }

  ngOnChanges(changes: SimpleChanges) {
     if (changes.model && !changes.model.firstChange && changes.model.currentValue !== changes.model.previousValue) {
        this.onModelChange(changes.model.currentValue);
     }
  }

  onModelChange(value: string): void {
    this.model = value;
    this.modelChange.emit(this.model);

    // Update validation state
    if (this.required) {
      const isValid = !!value && value.trim().length > 0;
      if (this.valid !== isValid) {
        this.valid = isValid;
        this.validChange.emit(this.valid);
      }
    }
  }

  selectDigit(digit: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    // Remove focus from the button
    if (event.target instanceof HTMLElement) {
      (event.target as HTMLElement).blur();
    }

    this.onModelChange(digit.toString());
    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }
  }

  onMenuOpened(): void {
    this.isOpen = true;
  }

  onMenuClosed(): void {
    this.isOpen = false;
  }
}

